'use client';

import * as React from 'react';
import { useEffect, useRef } from 'react';
import { cn } from '../utils/cn';
import { IconButton } from './icon-button';

export type ImagePreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  imageName?: string;
  className?: string;
};

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  imageName,
  className,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === modalRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center',
        'bg-black/80',
        'animate-in fade-in-0 duration-200',
        className
      )}
      onClick={handleBackdropClick}
      data-testid="image-preview-modal"
    >
      {/* Close button - positioned at top right */}
      <div className="absolute top-4 right-4 z-60">
        <IconButton
          aria-label="Close image preview"
          variant="ghost"
          size="lg"
          iconSize="lg"
          iconName="X"
          onClick={onClose}
          className="text-white hover:text-white/80 hover:bg-white/20 active:bg-white/30"
        />
      </div>

      {/* Image container */}
      <div className="relative max-w-[95vw] max-h-[95vh] flex flex-col">
        {/* Image title */}
        {imageName && (
          <div className="mb-4 text-center">
            <h3 className="text-white text-lg font-medium truncate max-w-96">{imageName}</h3>
          </div>
        )}

        {/* Image */}
        <div className="relative flex-1 flex items-center justify-center">
          <img
            ref={imageRef}
            src={imageUrl}
            alt={imageName || 'Preview'}
            className={cn(
              'max-w-full max-h-full object-contain',
              'rounded-lg shadow-2xl',
              'animate-in zoom-in-95 duration-200'
            )}
            onLoad={() => {
              // Focus the image for keyboard navigation
              imageRef.current?.focus();
            }}
            tabIndex={0}
          />
        </div>

        {/* Image info footer */}
        <div className="mt-4 text-center">
          <p className="text-white/70 text-sm">
            Press <kbd className="px-1 py-0.5 bg-white/20 rounded text-xs">Esc</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
};

ImagePreviewModal.displayName = 'ImagePreviewModal';
